import { useEffect, useState } from 'react';
import { useProjectsStore } from '../../../store/projects/projectsStore';
import { useUser } from '../../auth/hooks/useUser';

const isProject = (p) => p && typeof p === 'object';

/**
 * By id when we have one. Only without an id do we match the original folder
 * name (all consolidated-summary.json may carry) — with an id, a name match
 * could land on a different project.
 */
const findProject = (projects, projectId, folderName) => {
  if (!Array.isArray(projects)) return null;
  if (projectId) return projects.find(p => isProject(p) && p.id === projectId) || null;
  if (folderName) return projects.find(p => isProject(p) && p.name === folderName) || null;
  return null;
};

// One fetch at a time, however many components are asking
let inFlight = null;
const fetchProjects = (userData, force) => {
  if (!inFlight) {
    inFlight = useProjectsStore.getState().loadProjects(userData, force)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
};

/**
 * The name to show for a project — its alias (`displayName`) when it has been
 * renamed, exactly as the projects list shows it.
 *
 * The batch stream and consolidated-summary.json carry the project's original
 * folder name, which stays the S3 key after a rename. That name is still what
 * the API calls need, but it is never trusted for display: the name always
 * comes from the projects list. When the project is not in the list (a page
 * refresh, a page that never listed projects, a stale cache) the list is
 * fetched, and nothing is shown until it has been checked — so the stream's
 * old name never flashes up first. Only a project the list genuinely does not
 * hold falls back to the name it was given.
 */
const useProjectDisplayName = (projectId, fallbackName) => {
  const { userData } = useUser();
  const found = useProjectsStore((state) => findProject(state.projects, projectId, fallbackName));
  const [checkedKey, setCheckedKey] = useState(null);

  const key = `${projectId || ''}|${fallbackName || ''}`;
  const canFetch = !!userData?.accessToken;

  useEffect(() => {
    if (found || !canFetch || checkedKey === key || (!projectId && !fallbackName)) return undefined;
    let active = true;
    // Bypass the cache when it already answered without this project — a
    // cached list can predate a rename or the project itself
    const { cache } = useProjectsStore.getState();
    fetchProjects(userData, !!cache?.data)
      .catch(() => {})
      .finally(() => { if (active) setCheckedKey(key); });
    return () => { active = false; };
  }, [found, canFetch, checkedKey, key, userData]);

  if (found) return found.displayName || found.name || fallbackName;
  const settled = !canFetch || checkedKey === key;
  return settled ? fallbackName : null;
};

export default useProjectDisplayName;
